import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { CornerFrame, GoldDivider } from "../../components/BrandDecor/BrandDecor";
import {
  IconAdd,
  IconBadge,
  IconCalendar,
  IconCurrency,
  IconGlobe,
  IconRemove,
} from "../../assets/svgExport";
import SchemaMarkup, {
  breadcrumbSchema,
  serviceSchema,
  webPageSchema,
} from "../../components/SchemaMarkup/SchemaMarkup";
import { getPublishedProgramBySlug, getProgramPdfUrl } from "../../api/programs";
import { Program, RequirementBlock, RequirementCategory, RequirementList } from "../../admin/types";
import PhotoCarousel from "../../components/PhotoCarousel/PhotoCarousel";
import { renderRichText } from "../../utils/richText";
import { nvfoWechatQR, nvfoWhatsappQR } from "../../assets/imgExport";
import "./ProgramPage.scss";

const OVERVIEW_ICONS = [IconGlobe, IconCurrency, IconBadge, IconCalendar];

function imageByRole(program: Program, role: string): string | undefined {
  return program.images.find((img) => img.role === role)?.url;
}

// Live, admin-authored equivalent of the legacy hand-coded ProgramPage —
// renders whatever a Program row in the CMS actually contains instead of a
// hardcoded shape. Reachable as a fallback route for any slug that isn't one
// of the pre-existing hardcoded countries (see AppRoutes.js).
export default function ApiProgramPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [program, setProgram] = useState<Program | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setProgram(null);
    setError(null);
    getPublishedProgramBySlug(slug)
      .then(setProgram)
      .catch(() => setError("not found"));
  }, [slug]);

  async function handleDownloadPdf() {
    if (!slug) return;
    setDownloading(true);
    try {
      const url = await getProgramPdfUrl(slug);
      window.open(url, "_blank");
    } catch {
      alert("PDF生成失败，请稍后重试。");
    } finally {
      setDownloading(false);
    }
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-10 text-center">
        <h2 className="text-secondary-dark">未找到该项目</h2>
        <p className="text-secondary-light">该项目可能尚未发布，或链接有误。</p>
      </div>
    );
  }

  if (!program) {
    return <div className="min-h-[60vh] flex items-center justify-center text-secondary-light">加载中…</div>;
  }

  const programFullName = program.country + program.programTitle;
  const programTypeLabel = program.programType === "residence" ? "居留项目" : "护照项目";
  const programTypePath = program.programType === "residence" ? "residence" : "passport";
  const currentPath = `/program/${programTypePath}/${program.slug}`;
  const heroImg = imageByRole(program, "hero");
  const flagImg = imageByRole(program, "flag");
  const sceneryImg = imageByRole(program, "scenery") || heroImg;
  const galleryImgs = program.images.filter((img) => img.role === "gallery");
  const corePhotos = (["hero", "advantage", "eligibility"] as const)
    .map((role) => program.images.find((img) => img.role === role))
    .filter((img): img is NonNullable<typeof img> => !!img)
    .map((img) => ({ url: img.url, caption: img.caption || undefined }));

  return (
    <div className="font-sans flex flex-col items-center justify-center bg-white font-poster2">
      <SchemaMarkup
        data={[
          breadcrumbSchema([
            { name: "首页", path: "/" },
            { name: "身份规划", path: "/program" },
            { name: programTypeLabel, path: `/program#${programTypePath}` },
            { name: programFullName, path: currentPath },
          ]),
          webPageSchema({
            name: `${programFullName} | NVFO`,
            description: program.heroDescription || "",
            path: currentPath,
          }),
          serviceSchema({
            name: programFullName,
            description: program.heroDescription || "",
            path: currentPath,
          }),
        ]}
      />
      <Helmet>
        <title>{programFullName} | NVFO</title>
        {program.heroDescription && <meta name="description" content={program.heroDescription} />}
      </Helmet>

      {/* Hero */}
      <div className="relative min-h-[30rem] w-full overflow-hidden flex items-center justify-start px-10 sm:px-20 py-32">
        <CornerFrame />
        <div
          className="absolute inset-0 z-[1]"
          style={{
            background:
              "linear-gradient(90deg, rgba(35,45,57,0.92) 0%, rgba(35,45,57,0.75) 45%, rgba(35,45,57,0.35) 100%)",
          }}
        />
        {heroImg && (
          <img className="absolute h-full right-0 w-screen object-cover" src={heroImg} alt={programFullName} />
        )}
        <div className="relative z-10 text-white max-w-2xl">
          <p className="mb-6 inline-flex items-center gap-2 text-xs sm:text-sm tracking-[0.2em] uppercase text-gold-light border border-gold/40 rounded-full px-4 py-2 w-fit">
            {flagImg && <img className="w-4 h-4 rounded-full object-cover" src={flagImg} alt={program.country + " flag"} />}
            {program.heroBadge || programTypeLabel}
          </p>
          <h1 className="flex flex-col xxs:flex-row items-start xxs:items-center gap-3 xxs:gap-4 flex-wrap tracking-wide">
            <span className="font-bold text-white">{program.country}</span>
            <span className="font-sans text-gold">{program.programTitle}</span>
          </h1>
          <GoldDivider className="my-6" toneClassName="bg-gold/70" />
          <div className="mb-6 z-10 text-white flex gap-2 flex-wrap font-semibold font-sans text-sm">
            {program.investmentAmount && (
              <p className="bg-white/10 border border-white/20 rounded-full px-4 py-1.5 flex items-center gap-2">
                <IconCurrency width={16} height={16} fill="#e0ca9d" />
                {program.investmentAmount}
              </p>
            )}
            {program.durationLabel && (
              <p className="bg-white/10 border border-white/20 rounded-full px-4 py-1.5 flex items-center gap-2">
                <IconCalendar width={16} height={16} fill="#e0ca9d" />
                {program.durationLabel}
              </p>
            )}
            {(program.heroHighlights || []).map((item, i) => (
              <p key={i} className="bg-white/10 border border-white/20 rounded-full px-4 py-1.5">
                {item}
              </p>
            ))}
          </div>
          {program.heroDescription && (
            <p className="w-full opacity-80 leading-relaxed">{program.heroDescription}</p>
          )}
        </div>
      </div>

      <div className="w-full">
        {(program.trustStats || []).length > 0 && (
          <div className="w-full bg-secondary-dark py-10 px-10 sm:px-20">
            <div className="max-width-centered flex flex-wrap justify-center gap-x-16 gap-y-6">
              {program.trustStats!.map((stat, i) => (
                <div key={i} className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-gold">{stat.value}</p>
                  <p className="text-white/60 text-sm mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Overview — same background treatment as the PDF's overview page:
            the uploaded scenery photo fills the whole section, with a scrim
            fading from opaque (text side) to transparent (photo side). */}
        <div className="py-16 sm:py-32 relative w-full bg-c-gray overflow-hidden">
          {sceneryImg && <img className="absolute inset-0 w-full h-full object-cover" src={sceneryImg} alt={program.country + "'s scenery"} />}
          <div className="absolute inset-0 bg-gradient-to-r from-c-gray via-c-gray/85 to-c-gray/30" />
          <div className="w-[80%] md:w-[60%] xl:w-auto px-10 sm:px-20 xl:ml-auto xl:mr-auto max-w-[1240px] relative z-10">
            <SectionKicker label="国家概况" />
            <div className="flex items-center gap-4 mb-10">
              <h2 className="tracking-wide">{program.country}</h2>
              {flagImg && <img className="mt-1 h-8 rounded-sm object-cover border border-fourth-light" src={flagImg} alt={program.country + "'s flag"} />}
            </div>
            <div className="leading-relaxed text-secondary-dark/80">
              {(program.overviewDescription || []).map((item, i) => (
                <p key={i} className="mb-3">
                  {item}
                </p>
              ))}
            </div>
            {(program.overviewStats || []).length > 0 && (
              <div className="flex flex-col gap-4 mt-16 max-w-sm">
                {program.overviewStats!.map((stat, i) => {
                  const Icon = OVERVIEW_ICONS[i % OVERVIEW_ICONS.length];
                  return (
                    <div key={i} className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center shrink-0">
                        <Icon width={18} height={18} fill="#b89355" />
                      </div>
                      <div>
                        <p className="text-xs text-secondary-light">{stat.label}</p>
                        <p className="font-semibold text-secondary-dark">{stat.value}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {corePhotos.length > 1 && (
          <div className="py-10 sm:py-16 px-10 sm:px-20 bg-c-gray">
            <div className="max-width-centered">
              <PhotoCarousel images={corePhotos} />
            </div>
          </div>
        )}

        {galleryImgs.length > 0 && (
          <div className="px-10 sm:px-20 max-width-centered my-24">
            <SectionKicker label="项目实景" />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {galleryImgs.map((img) => (
                <img key={img.id} src={img.url} alt={img.caption || programFullName} className="w-full h-48 object-cover rounded-xl" />
              ))}
            </div>
          </div>
        )}

        {/* Requirements */}
        {(program.requirementGroups || []).length > 0 && (
          <div className="my-24 px-10 sm:px-20 flex flex-col md:flex-row gap-20 md:gap-0 max-width-centered">
            <div className="w-full md:w-[45%]">
              <SectionKicker label="申请条件" />
              <h2 className="leading-relaxed tracking-wide">
                <span>谁可以申请</span>
                <br />
                <span>{programFullName}？</span>
              </h2>
            </div>
            <div className="w-full md:w-[55%]">
              {(program.requirementGroups || []).map((block, i) => (
                <Requirement key={i} block={block} />
              ))}
            </div>
          </div>
        )}

        {/* Advantages */}
        {(program.advantages || []).length > 0 && (
          <div className="px-10 sm:px-20 max-width-centered">
            <div className="flex items-center gap-4 my-24 mb-3 mt-32 md:mt-24">
              <span className="h-[1px] w-10 bg-gold" />
              <p className="text-xs tracking-[0.2em] uppercase text-main/60">项目优势</p>
            </div>
            <h2 className="mb-14 tracking-wide">{programFullName}有什么优势？</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {program.advantages!.map((item, i) => (
                <div key={i} className="flex gap-5 bg-c-gray rounded-xl p-6">
                  <p className="font-main-serif text-3xl text-gold/50 shrink-0 w-10">{String(i + 1).padStart(2, "0")}</p>
                  <div>
                    <h3 className="text-lg font-semibold text-secondary-dark mb-1.5">{item.title}</h3>
                    <p className="text-secondary-dark/70 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Investment tiers + fee tables */}
        {((program.investmentTiers || []).length > 0 || (program.feeTables || []).length > 0) && (
          <div className="px-10 sm:px-20 max-width-centered my-24">
            <SectionKicker label="投资详情" />
            {program.investmentTitle && <h2 className="mb-4 tracking-wide">{program.investmentTitle}</h2>}
            {program.investmentNote && <p className="text-secondary-dark/70 mb-10 leading-relaxed max-w-2xl">{program.investmentNote}</p>}
            {(program.investmentTiers || []).length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
                {program.investmentTiers!.map((tier, i) => (
                  <div key={i} className="rounded-xl border border-fourth-light p-6 text-center">
                    <p className="text-sm text-secondary-light mb-2">{tier.label}</p>
                    <p className="text-xl font-bold text-gold">{tier.amount}</p>
                  </div>
                ))}
              </div>
            )}
            {(program.feeTables || []).map((table, i) => (
              <div key={i} className="mb-8">
                <h3 className="text-lg font-semibold text-secondary-dark mb-3">{table.title}</h3>
                <div className="rounded-xl border border-fourth-light overflow-hidden">
                  {table.rows.map((row, j) => (
                    <div
                      key={j}
                      className={`flex items-center justify-between px-6 py-3 text-sm ${j % 2 === 0 ? "bg-c-gray" : "bg-white"}`}
                    >
                      <span className="text-secondary-dark/80">{row.label}</span>
                      <span className="font-semibold text-secondary-dark">{row.amount}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Process */}
        {(program.processSteps || []).length > 0 && (
          <div className="px-10 sm:px-20 my-40 max-width-centered">
            <SectionKicker label="申请流程" />
            <h2 className="tracking-wide">{programFullName}申请流程</h2>
            {program.processDurationLabel && <p className="text-secondary-light text-lg mt-2 mb-14">{program.processDurationLabel}</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
              {program.processSteps!.map((step, i) => (
                <div key={i} className="rounded-xl bg-c-gray p-6">
                  <p className="font-main-serif text-2xl text-gold/50 mb-2">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="font-semibold text-secondary-dark mb-1.5">{step.title}</h3>
                  <p className="text-secondary-dark/70 text-sm leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="relative w-full bg-secondary-dark py-20 px-10 sm:px-20 mt-20 overflow-hidden">
          <CornerFrame inset="inset-6 sm:inset-8" />
          <div className="max-width-centered text-center">
            <p className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-gold-light border border-gold/40 rounded-full px-4 py-2 mb-6">
              AI 智能匹配
            </p>
            <h2 className="text-white mb-4 tracking-wide">{program.ctaTitle || `对${programFullName}感兴趣？`}</h2>
            <GoldDivider className="justify-center mb-6" toneClassName="bg-gold/60" />
            <p className="text-white/60 max-w-xl mx-auto leading-relaxed mb-10">
              {program.ctaDescription || "让 AI 智能匹配系统根据您的预算、目标与意向地区，为您定制专属方案；或直接联系我们的顾问团队一对一咨询。"}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate("/assessment")}
                className="bg-gold hover:bg-gold-light text-secondary-dark font-medium px-10 py-4 rounded-full transition-colors"
              >
                移民方案定制
              </button>
              <button
                onClick={handleDownloadPdf}
                disabled={downloading}
                className="border border-white/30 hover:border-gold text-white px-10 py-4 rounded-full transition-colors disabled:opacity-60"
              >
                {downloading ? "生成中…" : "下载PDF"}
              </button>
            </div>
            <div className="flex items-center justify-center gap-10 mt-10">
              <div className="flex flex-col items-center gap-2">
                <img
                  src={nvfoWechatQR}
                  alt="微信咨询二维码"
                  className="h-20 w-auto max-w-none"
                  style={{ filter: "invert(1)", mixBlendMode: "screen" }}
                />
                <span className="text-xs text-white/50">微信咨询</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <img
                  src={nvfoWhatsappQR}
                  alt="WhatsApp咨询二维码"
                  className="h-20 w-auto max-w-none"
                  style={{ filter: "invert(1)", mixBlendMode: "screen" }}
                />
                <span className="text-xs text-white/50">WhatsApp咨询</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionKicker({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-4 mb-3">
      <span className="h-[1px] w-10 bg-gold" />
      <p className="text-xs tracking-[0.2em] uppercase text-main/60">{label}</p>
    </div>
  );
}

const REQUIREMENT_OPTION_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

// No bullet markers -- either a numbered list (position shown as "1.", "2."
// ...) or a plain stacked list with no marker at all, matching the two
// styles the admin's requirements editor offers.
function RequirementListItems({ list }: { list: RequirementList }) {
  return (
    <ul className="space-y-1.5">
      {list.items.map((item, i) => (
        <li key={i} className="flex gap-2">
          {list.style === "numbered" && <span className="text-gold font-medium shrink-0">{i + 1}.</span>}
          <span>{renderRichText(item)}</span>
        </li>
      ))}
    </ul>
  );
}

// A single category's accordion -- title + collapsible body (list, or
// lettered routes A/B/C). Shared by a top-level category and by each
// category nested inside a section, so a section is just several of these
// grouped under one umbrella heading rather than a different component.
function CategoryAccordion({ category }: { category: RequirementCategory }) {
  const [expand, setExpand] = useState(true);
  return (
    <div className={`requirement mb-4 rounded-xl border ${expand ? "border-gold/30 bg-c-gray" : "border-fourth-light"} px-6 transition-colors`}>
      <h3 className="flex items-center justify-between hover:cursor-pointer py-5" onClick={() => setExpand(!expand)}>
        <span>{renderRichText(category.title)}</span>
        <span className="hover:cursor-pointer">
          {expand ? <IconRemove fill="#b89355" width={20} height={20} /> : <IconAdd fill="#b89355" width={20} height={20} />}
        </span>
      </h3>
      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${expand ? "max-h-[2000px] opacity-100 pb-5" : "max-h-0 opacity-0"}`}>
        {category.kind === "list" ? (
          <RequirementListItems list={category.list ?? { style: "numbered", items: [] }} />
        ) : (
          <div className="flex flex-col gap-4">
            {(category.options ?? []).map((option, oi) => (
              <div key={oi}>
                <p className="flex items-center gap-2 font-medium text-secondary-dark mb-1.5">
                  <span className="w-5 h-5 rounded-full bg-gold/15 border border-gold/40 text-gold text-xs flex items-center justify-center shrink-0 font-serif">
                    {REQUIREMENT_OPTION_LETTERS[oi] ?? oi + 1}
                  </span>
                  <span>{renderRichText(option.label)}</span>
                </p>
                <div className="pl-7">
                  <RequirementListItems list={option.list} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Requirement({ block }: { block: RequirementBlock }) {
  // A standalone note has no title to expand/collapse -- just a plain line
  // of text sitting between the categories around it.
  if (block.type === "note") {
    return <p className="text-sm text-secondary-dark/70 italic leading-relaxed mb-4">{renderRichText(block.text)}</p>;
  }

  // A section groups several categories (and, at the same level, standalone
  // notes) under one umbrella heading, the whole group set off with its own
  // background so it reads as one unit -- each category inside is still its
  // own independently expandable accordion.
  if (block.type === "section") {
    return (
      <div className="mb-6 rounded-xl bg-gold/5 border border-gold/20 p-5">
        <p className="text-xs tracking-[0.15em] uppercase text-gold font-semibold mb-3 pl-1">{renderRichText(block.title)}</p>
        {(block.items ?? []).map((item, ci) =>
          item.type === "note" ? (
            <p key={ci} className="text-sm text-secondary-dark/70 italic leading-relaxed mb-4 px-1">
              {renderRichText(item.text)}
            </p>
          ) : (
            <CategoryAccordion category={item} key={ci} />
          ),
        )}
      </div>
    );
  }

  return <CategoryAccordion category={block} />;
}
